import './id-card-upload.scss';

import Segmented from 'antd/es/segmented';
import SkeletonImage from 'antd/es/skeleton/Image';
import imageCompression, { type Options } from 'browser-image-compression';
import pdfMake from 'pdfmake/build/pdfmake.js';
import { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';

import { apiConnector } from '@/api/api.connector';
import CameraIcon from '@/media/icons/camera.svg?react';
import FileUploadIcon from '@/media/icons/file-upload.svg?react';
import TrashIcon from '@/media/icons/trash.svg?react';
import { isMobileDevice } from '@/shared/services/helpers/DOM';
import {
  calculateFileSizeInMB,
  convertFileToBase64
} from '@/shared/services/helpers/files';
import type { FileType } from '@/shared/types/files.types';

import Button from '../button';
import FileUpload from '../file-upload';
import Modal from '../modal';
import { rotateAndFitImage } from './helpers/rotate-and-fit-image';

interface IProps {
  file?: FileType;
  helperText?: string;
  error?: string;
  onChange: (file: Partial<FileType>) => void;
  onDelete: () => void;
  isFrozen?: boolean;
  isRequired?: boolean;
  compressOptions?: Options;
}

const isMobile = isMobileDevice();
const CARD_RADIO = { width: isMobile ? 54 : 85, height: isMobile ? 85 : 54 };

const IdCardUpload: React.FC<IProps> = ({
  file,
  helperText,
  error,
  onChange,
  onDelete,
  isFrozen,
  isRequired,
  compressOptions
}) => {
  const uploadTypeOptions = [
    { value: 'pdf', label: 'Загрузить PDF' },
    { value: 'img', label: 'Загрузить снимки' }
  ];

  const webCamRef = useRef<Webcam>(null);

  const [selectedMethod, setSelectedMethod] = useState<'camera' | 'upload'>(
    'camera'
  );
  const [selectedUploadType, setSelectedUploadType] = useState<'pdf' | 'img'>(
    'pdf'
  );
  const [cameraStep, setCameraStep] = useState<'front' | 'back'>('front');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [savedSideFiles, setSavedSideFiles] = useState<
    [FileType | null, FileType | null]
  >([null, null]);
  const [fileLoading, setFileLoading] = useState(false);

  const isShowDelete = !isFrozen && file;

  const handleSwitchMethod = () => {
    const updatedMethod = selectedMethod === 'camera' ? 'upload' : 'camera';
    setSelectedMethod(updatedMethod);
    setSelectedUploadType('pdf');
    setCameraStep('front');
    setSavedSideFiles([null, null]);
  };

  const handleChangeMethod = (newUploadType: string) => {
    setSelectedUploadType(newUploadType as typeof selectedUploadType);
    setSavedSideFiles([null, null]);
  };

  const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete();
  };

  const handleFileUploadClick = () => {
    setSelectedMethod('camera');
    setSelectedUploadType('pdf');
    setCameraStep('front');
    setSavedSideFiles([null, null]);
    setShowUploadModal(true);
  };

  const handleCloseModal = () => {
    setSelectedMethod('camera');
    setSelectedUploadType('pdf');
    setCameraStep('front');
    setSavedSideFiles([null, null]);
    setTimeout(() => {
      setShowUploadModal(false);
    }, 0);
  };

  const createAndSubmitPdf = async (targetSideFiles: typeof savedSideFiles) => {
    if (targetSideFiles[0] && targetSideFiles[1]) {
      const file1Format =
        targetSideFiles[0].name.split('.').pop()?.toLowerCase() || '';
      const file2Format =
        targetSideFiles[1].name.split('.').pop()?.toLowerCase() || '';

      const formattedImage1 = await rotateAndFitImage(
        targetSideFiles[0].base64
      );
      const formattedImage2 = await rotateAndFitImage(
        targetSideFiles[1].base64
      );

      const pdf = pdfMake.createPdf({
        pageSize: 'A4',
        compress: false,
        content: [
          {
            image: `data:image/${file1Format};base64,${formattedImage1.base64}`,
            marginBottom: 10,
            width: formattedImage1.width,
            height: formattedImage1.height,
            alignment: 'center'
          },
          {
            image: `data:image/${file2Format};base64,${formattedImage2.base64}`,
            width: formattedImage2.width,
            height: formattedImage2.height,
            alignment: 'center'
          }
        ]
      });
      pdf.getBase64().then((base64) => {
        pdf.getBlob().then((blob) => {
          const pdfFile = new File([blob], 'Удостоверение личности.pdf');
          onChange({
            base64,
            name: pdfFile.name,
            size: pdfFile.size,
            file: pdfFile
          });
          handleCloseModal();
        });
      });
    }
  };

  const handleTakeScreenShot = async () => {
    const webcam = webCamRef.current;
    if (webcam) {
      const imageSrc = webcam.getScreenshot();
      if (imageSrc) {
        const base64Response = await fetch(`${imageSrc}`);
        const blob = await base64Response.blob();
        const imageFile = new File([blob], `${cameraStep}.jpeg`, {
          type: 'image/jpeg'
        });
        const compressedFile = await imageCompression(imageFile, {
          ...compressOptions,
          ...(compressOptions?.maxSizeMB !== undefined
            ? { maxSizeMB: compressOptions.maxSizeMB / 2 }
            : {})
        });
        const compressedBase64 = await convertFileToBase64(compressedFile);
        const computedFile: FileType = {
          base64: compressedBase64[1],
          name: compressedFile.name,
          size: (compressedFile.size * 4) / 3,
          format: compressedBase64[0],
          file: compressedFile
        };
        const updatedSideFiles: typeof savedSideFiles = [...savedSideFiles];
        updatedSideFiles[cameraStep === 'front' ? 0 : 1] = computedFile;
        setCameraStep(cameraStep === 'front' ? 'back' : 'front');
        setSavedSideFiles(updatedSideFiles);
        if (cameraStep === 'back') {
          createAndSubmitPdf(updatedSideFiles);
        }
      }
    }
  };

  const handleUploadPdf = (result?: Partial<FileType>) => {
    if (!result) {
      return;
    }
    if (!result.base64 || !result.name || !result.size || !result.file) return;
    const computedFile: FileType = {
      base64: result.base64,
      name: result.name,
      size: result.size,
      file: result.file
    };

    onChange(computedFile);
    handleCloseModal();
  };

  const handleUploadImages =
    (isFront: boolean) => (result?: Partial<FileType>) => {
      if (
        !result ||
        !result.base64 ||
        !result.name ||
        !result.size ||
        !result.file
      ) {
        setSavedSideFiles((prev) => {
          const updatedSideFiles: typeof prev = [...prev];
          updatedSideFiles[isFront ? 0 : 1] = null;
          return updatedSideFiles;
        });
        return;
      }
      const computedFile: FileType = {
        base64: result.base64,
        name: result.name,
        size: result.size,
        file: result.file
      };
      setSavedSideFiles((prev) => {
        const updatedSideFiles: typeof prev = [...prev];
        updatedSideFiles[isFront ? 0 : 1] = computedFile;
        createAndSubmitPdf(updatedSideFiles);
        return updatedSideFiles;
      });
    };

  useEffect(() => {
    // Подгрузка файла и установка его в нужном формате в состояние формы
    const fetchBase64 = async () => {
      if (!(file && 'uuid' in file && file?.uuid)) return;
      setFileLoading(true);

      try {
        const fileUuid = 'uuid' in file ? (file.uuid as string) : '';
        const response = await apiConnector.downloadFile(fileUuid);

        const base64Response = await fetch(
          `data:image/webp;base64,${response.base64}`
        );
        const blob = await base64Response.blob();
        const imageFile = new File([blob], response.name);

        onChange({
          base64: response.base64,
          name: imageFile.name,
          size: imageFile.size,
          file: imageFile
        });
      } catch (e: unknown) {
        const { message } = e as { message: string };
        console.error(message);
      }
      setFileLoading(false);
    };

    if (file && 'uuid' in file && file?.uuid) {
      fetchBase64();
    }
  }, [file]);

  if (fileLoading) {
    return (
      <div className="id-card-upload">
        <div className="id-card-upload__shape">
          <SkeletonImage active />
        </div>
      </div>
    );
  }

  return (
    <div className="id-card-upload">
      <div
        className={`id-card-upload__view ${
          error ? 'id-card-upload__view--error' : ''
        }`}
      >
        <FileUploadIcon />

        <div className="id-card-upload__file-info">
          <p className="id-card-upload__title">
            Удостоверение личности
            {isRequired && <span className="id-card-upload__require">*</span>}
          </p>
          <p
            className={`id-card-upload__value ${
              !file ? 'id-card-upload__value--no-file' : ''
            }`}
          >
            {file?.name
              ? file.name
              : 'Сфотографируйте или загрузите удостоверение личности'}
            {file?.size && (
              <span className="id-card-upload__chosen-size">
                {calculateFileSizeInMB(file.size)} мб
              </span>
            )}
          </p>
        </div>

        {isShowDelete ? (
          <button onClick={handleRemoveFile} className="id-card-upload__delete">
            <TrashIcon />
          </button>
        ) : (
          !isFrozen && (
            <button
              type="button"
              onClick={handleFileUploadClick}
              className="id-card-upload__upload"
            />
          )
        )}
      </div>

      {error ? (
        <p className="file-upload__additional-text file-upload__additional-text--error">
          {error}
        </p>
      ) : (
        helperText && (
          <p className="file-upload__additional-text">{helperText}</p>
        )
      )}

      <Modal
        title="Загрузка удостоверения личности"
        open={showUploadModal}
        onClose={handleCloseModal}
      >
        {({ classes }) => (
          <>
            <div className={classes.form}>
              {selectedMethod === 'camera' ? (
                <div className="id-card-upload-modal__webcam-container">
                  <div className="id-card-upload-modal__webcam-wrapper">
                    <h4 className="id-card-upload-modal__title">
                      {cameraStep === 'front'
                        ? 'Лицевая сторона'
                        : 'Обратная сторона'}
                    </h4>
                    <div className="id-card-upload-modal__card-wrapper-outer">
                      <div className="id-card-upload-modal__card-wrapper-inner">
                        <div
                          className={`id-card-upload-modal__card-animation id-card-upload-modal__card-animation--${cameraStep}`}
                        />
                      </div>
                    </div>
                    {showUploadModal && (
                      <Webcam
                        audio={false}
                        disablePictureInPicture
                        frameBorder={'10px'}
                        ref={webCamRef}
                        screenshotFormat="image/jpeg"
                        style={{ borderRadius: '10px 10px 0 0' }}
                        videoConstraints={{
                          aspectRatio: CARD_RADIO.width / CARD_RADIO.height,
                          facingMode: 'environment',
                          height: {
                            exact: CARD_RADIO.height * 12,
                            ideal: CARD_RADIO.height * 12,
                            max: CARD_RADIO.height * 12,
                            min: CARD_RADIO.height * 12
                          },
                          width: {
                            exact: CARD_RADIO.width * 12,
                            ideal: CARD_RADIO.width * 12,
                            max: CARD_RADIO.width * 12,
                            min: CARD_RADIO.width * 12
                          }
                        }}
                        width={'100%'}
                      />
                    )}
                    <Button
                      type="button"
                      variant="filled-blue"
                      className="id-card-upload-modal__take-photo"
                      onClick={handleTakeScreenShot}
                    >
                      <span className="photo-upload__main-icon">
                        <CameraIcon />
                      </span>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Segmented
                    block
                    className="id-card-upload-modal__segmented"
                    value={
                      uploadTypeOptions.find(
                        (item) => item.value === selectedUploadType
                      )?.value
                    }
                    options={uploadTypeOptions}
                    onChange={handleChangeMethod}
                    size="large"
                  />
                  {selectedUploadType === 'pdf' ? (
                    <FileUpload
                      file={savedSideFiles[0] || undefined}
                      onChange={handleUploadPdf}
                      onDelete={() => {}}
                      isRequired
                      accept=".pdf"
                      allowedExtensions={['pdf']}
                    />
                  ) : (
                    <>
                      <FileUpload
                        title="Лицевая сторона"
                        file={savedSideFiles[0] || undefined}
                        onChange={handleUploadImages(true)}
                        onDelete={() => handleUploadImages(true)()}
                        isRequired
                        accept="image/*"
                        compressOptions={
                          compressOptions
                            ? {
                                ...compressOptions,
                                ...(compressOptions.maxSizeMB !== undefined
                                  ? { maxSizeMB: compressOptions.maxSizeMB / 2 }
                                  : {})
                              }
                            : undefined
                        }
                      />
                      <FileUpload
                        title="Обратная сторона"
                        file={savedSideFiles[1] || undefined}
                        onChange={handleUploadImages(false)}
                        onDelete={() => handleUploadImages(false)()}
                        isRequired
                        accept="image/*"
                        compressOptions={
                          compressOptions
                            ? {
                                ...compressOptions,
                                ...(compressOptions.maxSizeMB !== undefined
                                  ? { maxSizeMB: compressOptions.maxSizeMB / 2 }
                                  : {})
                              }
                            : undefined
                        }
                      />
                    </>
                  )}
                </>
              )}
            </div>
            <div className={classes.actions}>
              <Button type="button" onClick={handleSwitchMethod}>
                {selectedMethod === 'camera'
                  ? 'Выбрать файлы'
                  : 'Сделать фотографию'}
              </Button>
              <Button type="button" onClick={handleCloseModal}>
                Отмена
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default IdCardUpload;
